import {
  Accordion,
  AccordionSummary,
  Box,
  Chip,
  Dialog,
  Typography,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import CloneIconButton from "components/IconButtons/CloneIconButton";
import DeleteIconButton from "components/IconButtons/DeleteIconButton";
import EditIconButton from "components/IconButtons/EditIconButton";
import useBoolean from "hooks/useBoolean";
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
} from "react";
import {
  ConnectDragPreview,
  ConnectDragSource,
  ConnectDropTarget,
} from "react-dnd";
import { FormattedMessage as T } from "react-intl";
import { useQueryClient } from "react-query";
import {
  SkuContentRule,
  Skus,
} from "services/KnowfullyAdminClient";
import { DraggableInstance } from "utils/dndUtils";
import SkuRuleAccordionDetails from "./SkuRuleAccordionDetails";
import SkuRuleUpdateForm from "./SkuRuleUpdateForm";
import { SkuFormProps } from "types/sku-related-types";

const useStyles = makeStyles({
  editActions: {
    width: 100,
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  ruleBehavior: {
    width: 130,
    flexShrink: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  accessType: {
    width: 130,
    flexShrink: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  dragHandle: {
    width: 30,
    display: "flex",
    alignItems: "center",
    cursor: "move",
  },
});

export type DraggableSkuRuleProps = {
  index: number;
  skuRule: SkuContentRule;
  sku: Skus;
  toggleExpansion: () => void;
  expanded: boolean;
  onMoveSkuRule: (fromIndex: number, toIndex: number) => void;
  onDropSkuRule: () => void;
  cloneSkuRule: (skuRuleId: string) => Promise<void>;
  deleteSkuRule: (skuRuleId: string) => void;
  readonly?: boolean;
  isMembershipGatedAccessEnabled: boolean;
  // Supplied by react-dnd HOCs
  isDragging: boolean;
  connectDragSource: ConnectDragSource;
  connectDropTarget: ConnectDropTarget;
  connectDragPreview: ConnectDragPreview;
} & SkuFormProps;

const DraggableSkuRule = forwardRef<HTMLDivElement, DraggableSkuRuleProps>(
  (
    {
      skuRule,
      sku,
      cloneSkuRule,
      deleteSkuRule,
      expanded,
      toggleExpansion,
      isDragging,
      connectDragSource,
      connectDragPreview,
      connectDropTarget,
      readonly = false,
      isMembershipGatedAccessEnabled,
      onDirty,
      onClean,
      onSkuUpdated,
      index,
    },
    ref
  ) => {
    const styles = useStyles(useTheme());
    const queryClient = useQueryClient();
    const [updateOpen, updateOpenActions] = useBoolean(false);
    const [deleteOpen, deleteOpenActions] = useBoolean(false);
    const [cloning, cloningActions] = useBoolean(false);

    const elementRef = useRef(null);
    connectDragSource(elementRef);
    connectDropTarget(elementRef);
    connectDragPreview(connectDropTarget(elementRef));

    useImperativeHandle<any, DraggableInstance>(ref, () => ({
      getNode: () => elementRef.current,
    }));

    const opacity = isDragging ? 0 : 1;

    const onSkuRuleUpdated = () => {
      queryClient.invalidateQueries(["skuRules", sku.id]);
      updateOpenActions.setFalse();
    };

    const handleDelete = useCallback(() => {
      const id = skuRule?.id;
      deleteOpenActions.setFalse();
      if (id) deleteSkuRule(id);
    }, [skuRule?.id, deleteOpenActions, deleteSkuRule]);

    const cloneRule = useCallback(
      async (e: any) => {
        e.stopPropagation();
        const id = skuRule?.id;
        if (id) {
          cloningActions.setTrue();
          try {
            await cloneSkuRule(id);
          } finally {
            cloningActions.setFalse();
          }
        }
      },
      [skuRule?.id, cloningActions, cloneSkuRule]
    );

    return (
      <Box mb={2}>
        <div
          ref={elementRef}
          style={{ opacity, transform: "translate3d(0, 0, 0)" }}
        >
          <Accordion square expanded={expanded} onChange={toggleExpansion}>
            <AccordionSummary
              style={{ margin: 0 }}
              expandIcon={<ExpandMoreIcon />}
            >
              <Box display="flex" alignItems="center" flex={1}>
                {!readonly &&
                  connectDragSource(
                    <div>
                      <Box sx={styles.dragHandle}>
                        <DragHandleIcon />
                      </Box>
                    </div>
                  )}
                <Typography
                  variant="body2"
                  sx={{ width: 30, textAlign: "center", color: "text.secondary" }}
                >
                  {index + 1}
                </Typography>
                <Box flexGrow={1} display="flex" flexDirection="column">
                  <Typography>{skuRule.name}</Typography>
                  <Typography variant="caption">
                    <T
                      id="skuRuleTypeLabel"
                      values={{ name: skuRule.ruleType?.name }}
                    />
                  </Typography>
                </Box>
                <Box sx={styles.ruleBehavior}>
                  <Chip
                    icon={
                      skuRule.excludeFilter ? (
                        <RemoveCircleOutlineIcon />
                      ) : (
                        <AddCircleOutlineIcon />
                      )
                    }
                    label={
                      skuRule.excludeFilter ? (
                        <T id="skuRuleExcludeLabel" />
                      ) : (
                        <T id="skuRuleIncludeLabel" />
                      )
                    }
                    size="small"
                    variant="outlined"
                    color={skuRule.excludeFilter ? "default" : "success"}
                  />
                </Box>
                <Box sx={styles.accessType}>
                  {!skuRule.excludeFilter && skuRule.accessTypeName ? (
                    <Chip
                      icon={
                        skuRule.accessTypeName?.toLowerCase() === "open" ? (
                          <LockOpenIcon />
                        ) : (
                          <LockIcon />
                        )
                      }
                      label={skuRule.accessTypeName}
                      size="small"
                      variant="outlined"
                      color="info"
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </Box>
                <Box sx={styles.editActions}>
                  <CloneIconButton
                    size="small"
                    onClick={cloneRule}
                    disabled={readonly || cloning}
                  />
                  <EditIconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      updateOpenActions.setTrue();
                    }}
                    disabled={readonly}
                  />
                  <DeleteIconButton
                    size="small"
                    onClick={() => deleteOpenActions.setTrue()}
                    disabled={readonly}
                  />
                </Box>
              </Box>
            </AccordionSummary>

            {expanded && (
              <SkuRuleAccordionDetails
                sku={sku}
                skuRule={skuRule}
                readonly={readonly}
                index={index}
                onDirty={onDirty}
                onClean={onClean}
                onSkuUpdated={onSkuUpdated}
              />
            )}
          </Accordion>
        </div>

        <DeleteEntityDialog
          dialogTitleKey="skuRuleDeleteTitle"
          contentKey="skuRuleDeleteQuestion"
          open={deleteOpen}
          handleDelete={handleDelete}
          onCancel={() => deleteOpenActions.setFalse()}
        />

        <Dialog open={updateOpen} maxWidth="sm" fullWidth disableEnforceFocus>
          <SkuRuleUpdateForm
            onSkuRuleUpdated={onSkuRuleUpdated}
            onCancel={updateOpenActions.setFalse}
            sku={sku}
            skuRule={skuRule}
            isMembershipGatedAccessEnabled={isMembershipGatedAccessEnabled}
          />
        </Dialog>
      </Box>
    );
  }
);

export default DraggableSkuRule;
